import React, { useState } from 'react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Transaction } from '../../types/financial';
import { formatCurrency } from '../../utils/pdfGenerator';
import './Financial.css';

interface TransactionListProps {
  transactions: Transaction[];
  loading?: boolean;
  onEdit: (transaction: Transaction) => void;
  onDelete: (transactionId: string) => Promise<void>;
  filters?: {
    type?: 'receita' | 'despesa';
    categoryId?: string;
    accountId?: string;
    status?: 'pendente' | 'pago' | 'cancelado';
  };
  onFilterChange?: (filters: any) => void;
}

export const TransactionList: React.FC<TransactionListProps> = ({
  transactions,
  loading,
  onEdit,
  onDelete,
  filters,
  onFilterChange,
}) => {
  const [localFilters, setLocalFilters] = useState(filters || {});
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const handleFilterChange = (key: string, value: string) => {
    const newFilters = { ...localFilters, [key]: value || undefined };
    setLocalFilters(newFilters);
    if (onFilterChange) {
      onFilterChange(newFilters);
    }
  };

  const handleDelete = async (transaction: Transaction) => {
    if (!window.confirm(`Tem certeza que deseja excluir esta transação?\n\n${transaction.description}\n${formatCurrency(transaction.amount)}`)) {
      return;
    }

    setDeletingId(transaction.id);
    try {
      await onDelete(transaction.id);
    } catch (error: any) {
      alert(error.message || 'Erro ao excluir transação');
    } finally {
      setDeletingId(null);
    }
  };

  if (loading) {
    return (
      <Card title="Transações">
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <p>Carregando transações...</p>
        </div>
      </Card>
    );
  }

  return (
    <Card
      title="Transações"
      actions={
        <div className="ns-financial-filters">
          <select
            className="ns-input"
            value={localFilters.type || ''}
            onChange={(e) => handleFilterChange('type', e.target.value)}
            style={{ width: 'auto', padding: '8px' }}
          >
            <option value="">Todos os tipos</option>
            <option value="receita">Receitas</option>
            <option value="despesa">Despesas</option>
          </select>

          <select
            className="ns-input"
            value={localFilters.status || ''}
            onChange={(e) => handleFilterChange('status', e.target.value)}
            style={{ width: 'auto', padding: '8px' }}
          >
            <option value="">Todos os status</option>
            <option value="pendente">Pendente</option>
            <option value="pago">Pago</option>
            <option value="cancelado">Cancelado</option>
          </select>
        </div>
      }
    >
      {transactions.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <p>Nenhuma transação encontrada.</p>
        </div>
      ) : (
        <div className="ns-table-wrapper">
          <table className="ns-table ns-financial-consultations-table">
            <thead>
              <tr>
                <th>Data</th>
                <th>Descrição</th>
                <th>Categoria</th>
                <th>Tipo</th>
                <th>Valor</th>
                <th>Status</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {transactions.map((transaction) => (
                <tr key={transaction.id}>
                  <td>
                    {new Date(transaction.date).toLocaleDateString('pt-BR', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                    })}
                  </td>
                  <td>{transaction.description}</td>
                  <td>{transaction.categoryName}</td>
                  <td>
                    <span
                      className={`ns-financial-type-badge ${
                        transaction.type === 'receita' ? 'receita' : 'despesa'
                      }`}
                    >
                      {transaction.type === 'receita' ? 'Receita' : 'Despesa'}
                    </span>
                  </td>
                  <td
                    style={{
                      color: transaction.type === 'receita' ? '#10b981' : '#ef4444',
                      fontWeight: 600,
                    }}
                  >
                    {transaction.type === 'receita' ? '+' : '-'}
                    {formatCurrency(transaction.amount)}
                  </td>
                  <td>
                    <span
                      className={`ns-financial-status-badge ${
                        transaction.status === 'pago'
                          ? 'pago'
                          : transaction.status === 'pendente'
                          ? 'pendente'
                          : 'cancelado'
                      }`}
                    >
                      {transaction.status === 'pago'
                        ? 'Pago'
                        : transaction.status === 'pendente'
                        ? 'Pendente'
                        : 'Cancelado'}
                    </span>
                  </td>
                  <td>
                    <div className="ns-financial-actions">
                      <Button
                        variant="ghost"
                        onClick={() => onEdit(transaction)}
                        style={{ fontSize: '12px', padding: '4px 8px' }}
                      >
                        Editar
                      </Button>
                      <Button
                        variant="ghost"
                        onClick={() => handleDelete(transaction)}
                        disabled={deletingId === transaction.id}
                        style={{
                          fontSize: '12px',
                          padding: '4px 8px',
                          color: '#ef4444',
                        }}
                      >
                        {deletingId === transaction.id ? 'Excluindo...' : 'Excluir'}
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
};

